import { useState } from 'react';
import type { JSX } from 'react';

import { SectionBlock } from 'shared/ui';
import type { SnackbarVariant } from 'shared/ui';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';

import {
  useCreateWebhookSlugMutation,
  useCreateWebhookTokenMutation,
  useDeleteWebhookTokenMutation,
  useGetCredentialsQuery,
  useUpdateCredentialMutation,
} from '../api/integrations';
import { sendWebhookTest } from '../api/webhookTest';
import type { WebhookTestResponse } from '../api/webhookTest';
import { ID_TEXT_CLASS } from '../lib/id';
import { formatJson, parseJsonObject } from '../lib/json';
import { buildWebhookUrl, isBcryptHash } from '../lib/webhook';
import { PROVIDER_CATALOG, WEBHOOK_TOKEN_SECRET, getWebhookResources } from '../model/integration';
import type { IntegrationDetail } from '../model/integration';
import FormDialog, { Field } from './FormDialog';

type Notify = (message: string, variant?: SnackbarVariant) => void;

type DialogState =
  | { mode: 'slug' }
  | { mode: 'issueToken' }
  | { mode: 'showToken'; token: string }
  | { mode: 'useHash' }
  | { mode: 'removeToken' }
  | { mode: 'test'; resource: string };

const copyText = async (text: string, what: string, notify: Notify): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
    notify(`${what} copiado`);
  } catch {
    notify('No se pudo copiar: selecciónalo y cópialo a mano', 'error');
  }
};

interface WebhookTestDialogProps {
  integration: IntegrationDetail;
  resource: string;
  requiresToken: boolean;
  onClose: () => void;
}

/*
Sends a payload to the integration's public URL, as the external system would, and shows the answer. A dry run is the
default and works while the process is off; a real call is only for a process that is on.
*/
const WebhookTestDialog = ({ integration, resource, requiresToken, onClose }: WebhookTestDialogProps): JSX.Element => {
  const example = PROVIDER_CATALOG[integration.provider]?.webhookPayloadExamples?.[resource] ?? {};
  const [payload, setPayload] = useState(formatJson(example));
  const [token, setToken] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<WebhookTestResponse | null>(null);

  const process = integration.processes.find(({ type }) => type === `${resource}.webhook`);
  const url = buildWebhookUrl(integration.webhookSlug ?? '', resource, integration.webhookBaseUrl);

  const handleSubmit = async (): Promise<void> => {
    setError(null);
    setResponse(null);
    const parsed = parseJsonObject(payload);
    if (parsed.error !== null) {
      setError(`Payload: ${parsed.error}`);
      return;
    }
    setIsSending(true);
    try {
      setResponse(await sendWebhookTest({ url, payload: parsed.value, token: token.trim(), dryRun }));
    } catch (err) {
      setError(extractErrorMessage(err, 'No se pudo llamar al webhook'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <FormDialog
      title={`Probar ${resource}`}
      description={
        <>
          Llama a <span className="break-all font-mono text-xs">{url}</span> igual que el sistema externo.
          {!process ? ' La integración no tiene el proceso de este recurso, así que responde 404.' : null}
        </>
      }
      submitLabel={dryRun ? 'Enviar dry run' : 'Enviar real'}
      destructive={!dryRun}
      isSaving={isSending}
      error={error}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <Field label="Payload" htmlFor="webhook-test-payload">
        <Textarea
          id="webhook-test-payload"
          value={payload}
          onChange={(e) => setPayload(e.target.value)}
          className="min-h-48 font-mono text-xs"
        />
      </Field>
      <Field
        label="Token"
        htmlFor="webhook-test-token"
        hint={
          requiresToken
            ? 'El token en texto plano que usa el sistema externo. No se guarda.'
            : 'El webhook es público: no necesita token.'
        }
      >
        <Input
          id="webhook-test-token"
          type="password"
          autoComplete="off"
          value={token}
          disabled={!requiresToken}
          onChange={(e) => setToken(e.target.value)}
        />
      </Field>
      <div className="flex items-start gap-2">
        <Checkbox
          id="webhook-test-dry-run"
          checked={dryRun}
          onCheckedChange={(checked) => setDryRun(checked === true)}
        />
        <div className="flex flex-col gap-0.5">
          <Label htmlFor="webhook-test-dry-run">Dry run</Label>
          <p className="m-0 text-xs text-text-muted">
            {dryRun
              ? 'Dice qué haría sin escribir nada. Funciona con el proceso apagado.'
              : 'Corre de verdad: crea o actualiza el cliente y le envía el template. Con el proceso apagado se ignora.'}
          </p>
        </div>
      </div>
      {response ? (
        <div className="flex flex-col gap-1">
          <span
            className={`text-sm font-medium ${response.status >= 200 && response.status < 300 ? 'text-success' : 'text-error'}`}
          >
            HTTP {response.status}
          </span>
          <pre className="m-0 max-h-60 overflow-auto rounded bg-bg-surface-raised p-2 text-[11px]">
            {typeof response.body === 'string' ? response.body : formatJson(response.body)}
          </pre>
        </div>
      ) : null}
    </FormDialog>
  );
};

interface WebhookSectionProps {
  integration: IntegrationDetail;
  notify: Notify;
}

/*
The integration's public URLs (POST /webhooks/<slug>/<resource>) and how they authenticate. The token lives in the
credential, so it covers every integration that shares it; the slug belongs to this integration only.
*/
const WebhookSection = ({ integration, notify }: WebhookSectionProps): JSX.Element => {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [hash, setHash] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { provider, credential, webhookSlug } = integration;
  const resources = getWebhookResources(provider);
  const { data: credentials = [] } = useGetCredentialsQuery(provider, { skip: !credential });
  const hasToken = !!credentials.find(({ id }) => id === credential?.id)?.secrets?.[WEBHOOK_TOKEN_SECRET];

  const [createWebhookSlug, { isLoading: isCreatingSlug }] = useCreateWebhookSlugMutation();
  const [createWebhookToken, { isLoading: isIssuingToken }] = useCreateWebhookTokenMutation();
  const [deleteWebhookToken, { isLoading: isRemovingToken }] = useDeleteWebhookTokenMutation();
  const [updateCredential, { isLoading: isSavingHash }] = useUpdateCredentialMutation();
  const isSaving = isCreatingSlug || isIssuingToken || isRemovingToken || isSavingHash;

  const open = (state: DialogState): void => {
    setError(null);
    setHash('');
    setDialog(state);
  };
  const close = (): void => setDialog(null);

  const run = async (action: () => Promise<void>, fallback: string): Promise<void> => {
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(extractErrorMessage(err, fallback));
    }
  };

  const generateSlug = (): Promise<void> =>
    run(async () => {
      await createWebhookSlug(integration.id).unwrap();
      notify(webhookSlug ? 'URL rotada: la anterior ya no responde' : 'URL generada');
      close();
    }, 'No se pudo generar la URL');

  const issueToken = (): Promise<void> =>
    run(async () => {
      if (!credential) return;
      const { token } = await createWebhookToken({ credentialId: credential.id, provider }).unwrap();
      setDialog({ mode: 'showToken', token });
    }, 'No se pudo generar el token');

  const saveHash = (): Promise<void> =>
    run(async () => {
      if (!credential) return;
      if (!isBcryptHash(hash.trim())) {
        setError('No parece un hash bcrypt: debe empezar con $2a$, $2b$ o $2y$ y tener 60 caracteres');
        return;
      }
      await updateCredential({
        id: credential.id,
        provider,
        body: { secrets: { webhook: { tokenHash: hash.trim() } } },
      }).unwrap();
      notify('Hash guardado: los webhooks exigen ese token');
      close();
    }, 'No se pudo guardar el hash');

  const removeToken = (): Promise<void> =>
    run(async () => {
      if (!credential) return;
      await deleteWebhookToken({ credentialId: credential.id, provider }).unwrap();
      notify('Token quitado: los webhooks quedan públicos');
      close();
    }, 'No se pudo quitar el token');

  return (
    <SectionBlock
      title="Webhook"
      action={
        <Button size="sm" variant="outline" onClick={() => open({ mode: 'slug' })}>
          {webhookSlug ? 'Rotar URL' : 'Generar URL'}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-text-muted">Autenticación</span>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              {hasToken ? (
                <>
                  <Badge variant="secondary">Bearer token</Badge>
                  <span className="text-text-muted">El sistema externo envía Authorization: Bearer &lt;token&gt;</span>
                </>
              ) : (
                <>
                  <Badge variant="destructive">Pública</Badge>
                  <span className="text-text-muted">Solo el slug de la URL protege el webhook</span>
                </>
              )}
            </div>
            {!credential ? (
              <span className="text-xs text-text-muted">
                Asigna una credencial a la integración para usar un token.
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" disabled={!credential} onClick={() => open({ mode: 'issueToken' })}>
              {hasToken ? 'Rotar token' : 'Generar token'}
            </Button>
            <Button size="sm" variant="outline" disabled={!credential} onClick={() => open({ mode: 'useHash' })}>
              Usar hash existente
            </Button>
            {hasToken ? (
              <Button size="sm" variant="outline" onClick={() => open({ mode: 'removeToken' })}>
                Quitar token
              </Button>
            ) : null}
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Recurso</TableHead>
              <TableHead>Proceso</TableHead>
              <TableHead>URL</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {resources.map((resource) => {
              const process = integration.processes.find(({ type }) => type === `${resource}.webhook`);
              const url = webhookSlug ? buildWebhookUrl(webhookSlug, resource, integration.webhookBaseUrl) : null;
              return (
                <TableRow key={resource} className="align-top">
                  <TableCell className="font-mono text-xs">{resource}</TableCell>
                  <TableCell>
                    {process ? (
                      <Badge variant={process.enabled ? 'secondary' : 'outline'}>
                        {process.enabled ? 'Activo' : 'Inactivo'}
                      </Badge>
                    ) : (
                      <span className="text-xs text-text-muted">No creado</span>
                    )}
                  </TableCell>
                  <TableCell className="max-w-96 whitespace-normal">
                    {url ? (
                      <span className="break-all font-mono text-xs">{url}</span>
                    ) : (
                      <span className="text-xs text-text-muted">Genera la URL primero</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!url}
                        onClick={() => url && copyText(url, 'URL', notify)}
                      >
                        Copiar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!url}
                        onClick={() => open({ mode: 'test', resource })}
                      >
                        Probar
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {dialog?.mode === 'slug' ? (
        <FormDialog
          title={webhookSlug ? 'Rotar URL' : 'Generar URL'}
          description={
            webhookSlug ? (
              <>
                Cambia el slug y, con él, la URL de cada webhook de esta integración. La URL actual deja de responder en
                cuanto guardes: <strong>un sistema externo que la usa deja de llegar a Lidz</strong> hasta que configure
                la nueva, así que avísale antes. Las integraciones que entran por su ruta antigua (por ejemplo
                /sci-echeverria/prospect-webhook) siguen funcionando, porque esa ruta busca la integración, no la URL.
              </>
            ) : (
              'Crea la URL pública de cada webhook de esta integración. El final aleatorio la hace imposible de adivinar.'
            )
          }
          submitLabel={webhookSlug ? 'Rotar' : 'Generar'}
          destructive={!!webhookSlug}
          isSaving={isSaving}
          error={error}
          onClose={close}
          onSubmit={generateSlug}
        />
      ) : null}

      {dialog?.mode === 'issueToken' && credential ? (
        <FormDialog
          title={hasToken ? 'Rotar token' : 'Generar token'}
          description={
            <>
              {hasToken
                ? 'El token actual deja de funcionar: el sistema externo no entra hasta que use el nuevo. '
                : 'Desde ahora el webhook exige Authorization: Bearer <token>. '}
              Aplica a todas las integraciones que usan la credencial {credential.label}{' '}
              <span className={ID_TEXT_CLASS}>#{credential.id}</span>. Si el sistema externo ya tiene un token, usa
              «Usar hash existente».
            </>
          }
          submitLabel={hasToken ? 'Rotar' : 'Generar'}
          destructive={hasToken}
          isSaving={isSaving}
          error={error}
          onClose={close}
          onSubmit={issueToken}
        />
      ) : null}

      {dialog?.mode === 'showToken' ? (
        <FormDialog
          title="Token generado"
          description="Cópialo ahora y entrégalo al sistema externo: no se vuelve a mostrar, solo se guarda su hash."
          submitLabel="Listo"
          isSaving={false}
          error={null}
          onClose={close}
          onSubmit={close}
        >
          <div className="flex gap-2">
            <Input readOnly value={dialog.token} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
            <Button type="button" variant="outline" onClick={() => copyText(dialog.token, 'Token', notify)}>
              Copiar
            </Button>
          </div>
        </FormDialog>
      ) : null}

      {dialog?.mode === 'useHash' && credential ? (
        <FormDialog
          title="Usar hash existente"
          description="Para que el webhook acepte el token que el sistema externo ya envía, por ejemplo el de una ruta antigua. Solo se guarda el hash, nunca el token."
          submitLabel="Guardar"
          isSaving={isSaving}
          error={error}
          onClose={close}
          onSubmit={saveHash}
        >
          <Field
            label="Hash bcrypt del token"
            htmlFor="webhook-token-hash"
            hint="El valor de la variable de entorno de la ruta antigua, ej. SCI_RVC_TOKEN. Empieza con $2a$ o $2b$."
          >
            <Input
              id="webhook-token-hash"
              autoComplete="off"
              value={hash}
              onChange={(e) => setHash(e.target.value)}
              className="font-mono text-xs"
            />
          </Field>
        </FormDialog>
      ) : null}

      {dialog?.mode === 'removeToken' && credential ? (
        <FormDialog
          title="Quitar token"
          description={`Los webhooks de todas las integraciones con la credencial #${credential.id} quedan públicos: solo el slug de la URL los protege.`}
          submitLabel="Quitar"
          destructive
          isSaving={isSaving}
          error={error}
          onClose={close}
          onSubmit={removeToken}
        />
      ) : null}

      {dialog?.mode === 'test' ? (
        <WebhookTestDialog
          integration={integration}
          resource={dialog.resource}
          requiresToken={hasToken}
          onClose={close}
        />
      ) : null}
    </SectionBlock>
  );
};

export default WebhookSection;
